import {
  ID,
  noDeadlineSectionID,
  overdueSectionID,
  ReorderItem,
  ReorderResult,
  Task,
  TaskListItemTypes,
  UpdateTask,
  WeekDays,
} from '@griply/types';
import { endOfDay, isBefore, startOfDay } from 'date-fns';
import cloneDeep from 'lodash/cloneDeep';

import { parseDateKey } from '../format-date-key';
import { getDueTaskPlanning, getTaskPlanning } from '../get-task-planning';
import { createPlannedOccurrenceForDate } from '../schedules';
import { getSectionIdFromEncodedTaskId } from './encoded-task-id';
import { filterReorderableChildIds } from './filter-reorderable-child-ids';
import { findParentItem } from './find-parent-item';
import { getTaskIdFromTreeId } from './get-task-id-from-tree-id';

export type OnDeadlineReorderOptions = {
  updatedItems: readonly ReorderItem[];
  oldItems: readonly ReorderItem[];
  tasksMap: Record<ID, Task>;
  today: Date;
  weekStartsOn: WeekDays;
  taskIdResolver?: (id: ID) => ID;
};

export const onDeadlineReorder = ({
  updatedItems,
  oldItems,
  tasksMap,
  today,
  weekStartsOn,
  taskIdResolver = getTaskIdFromTreeId,
}: OnDeadlineReorderOptions): ReorderResult => {
  const updatedTasks: UpdateTask[] = [];
  const updatedSections: ReorderResult['updatedSections'] = [];

  updatedItems.forEach((item) => {
    if (item.type === TaskListItemTypes.Task) {
      const taskId = taskIdResolver(item.id);
      if (!tasksMap[taskId]) {
        return;
      }

      const task = tasksMap[taskId];
      const taskToUpdate = cloneDeep(task);

      const newSectionItem = findParentItem(updatedItems, item.id);
      if (!newSectionItem) {
        return;
      }

      // When the task appears under multiple date sections it carries a
      // per-section encoded id (`<sectionId>--<taskId>`); the source section is
      // the encoded prefix. Raw ids fall back to the old-tree parent lookup.
      const oldSectionId =
        getSectionIdFromEncodedTaskId(item.id) ??
        findParentItem(oldItems, item.id)?.id;
      if (!oldSectionId) {
        return;
      }

      const newSectionId = newSectionItem.id;

      if (newSectionId === oldSectionId) {
        return;
      }

      if (newSectionId === overdueSectionID) {
        return;
      }

      if (newSectionId === noDeadlineSectionID) {
        if (!task.schedules?.length) {
          taskToUpdate.endStrategy = {
            deadline: null,
            completionCount: task.endStrategy?.completionCount ?? null,
          };
          updatedTasks.push(taskToUpdate);
        }
        return;
      }

      const newDate = parseDateKey(newSectionId);
      const newDeadline = endOfDay(newDate);

      if (task.schedules?.length) {
        const resolveOldOccurrence = () => {
          if (oldSectionId === overdueSectionID) {
            const planning = getDueTaskPlanning(task, {
              date: today,
              today,
              weekStartsOn,
            });
            const occurrence = planning.find(({ date }) =>
              isBefore(date, today),
            );
            return { oldDate: occurrence?.date ?? today, occurrence };
          }
          const date = parseDateKey(oldSectionId);
          const [occurrence] = getTaskPlanning(task, {
            startDate: startOfDay(date),
            endDate: endOfDay(date),
            today,
            weekStartsOn,
          });
          return { oldDate: date, occurrence };
        };
        const { oldDate, occurrence: plannedOccurrence } =
          resolveOldOccurrence();

        const schedules = createPlannedOccurrenceForDate(
          task.schedules,
          newDeadline,
          {
            timeslot: plannedOccurrence?.timeslot ?? null,
            originalDate: oldDate,
            today,
            weekStartsOn,
          },
        );

        updatedTasks.push({ id: task.id, schedules });
        return;
      }

      taskToUpdate.endStrategy = {
        deadline: newDeadline,
        completionCount: taskToUpdate.endStrategy?.completionCount ?? null,
      };
      taskToUpdate.schedules = null;
      updatedTasks.push(taskToUpdate);
      return;
    }

    if (item.type === TaskListItemTypes.Date) {
      updatedSections.push({
        id: item.id,
        taskSorting: filterReorderableChildIds(
          item.children?.map(taskIdResolver),
        ),
      });
    }
  });

  return {
    updatedSections,
    updatedTasks,
  };
};
